import {
  DropdownMenu,
  EllipsisVerticalIcon,
  IconButton,
} from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import { useTagsQuery } from '~publish/hooks/useTags'

import { ChannelFilterSubMenu } from './ChannelFilterSubMenu'
import { TimezoneSubMenu } from './TimezoneSubMenu'
import { TagFilterSubMenu } from '../Channel/TagFilterSubMenu'

import type { JSX } from 'react'

export type QueueActionsProps = Omit<ButtonProps, 'children'> & {
  channels?: MaskedFilterByChannelFragment
  selectedChannels?: string[]
  onChannelSelect?: (channelIds: string[]) => void
  selectedTags?: string[]
  onTagSelect?: (tagIds: string[]) => void
  triggerIcon?: React.ReactElement
  /**
   * Channel IDs to render disabled in the channel filter (channels the actor
   * can't access). Forwarded to {@link ChannelFilterSubMenu}.
   */
  disabledChannelIds?: Set<string>
  /** Shows the "No channels" row in the channel filter. Forwarded to {@link ChannelFilterSubMenu}. */
  showNoChannelsOption?: boolean
}

/**
 * Dropdown menu with filters and timezone info for AllChannels page.
 */
export function QueueActions({
  channels,
  selectedChannels = [],
  onChannelSelect,
  selectedTags = [],
  onTagSelect,
  triggerIcon,
  disabledChannelIds,
  showNoChannelsOption,
  ...buttonProps
}: QueueActionsProps): JSX.Element {
  const { tags } = useTagsQuery()

  const showChannelFilter = channels && channels.length > 0 && onChannelSelect
  const showTagFilter = tags.length > 0 && onTagSelect
  const showFilters = showChannelFilter || showTagFilter

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton variant="tertiary" label="More actions" {...buttonProps}>
          {triggerIcon ?? <EllipsisVerticalIcon />}
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        {showChannelFilter && (
          <ChannelFilterSubMenu
            channels={channels}
            selectedChannelIds={selectedChannels}
            onChannelSelect={onChannelSelect}
            disabledChannelIds={disabledChannelIds}
            showNoChannelsOption={showNoChannelsOption}
          />
        )}

        {showTagFilter && (
          <TagFilterSubMenu
            selectedTags={selectedTags}
            onTagSelect={onTagSelect}
          />
        )}

        {showFilters && <DropdownMenu.Separator />}

        <TimezoneSubMenu />
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
